/**
 * 订单与商品：订单列表与详情（退款、与支付通道同步）、充值商品、每日对账结果。
 *
 * 背景：PRD 6.7、8 章：用户经 Stripe Checkout 充值，Webhook 与定时回查推进订单；退款按比例追回花瓣，
 *   余额可能变负；每日对账比对通道流水与本地订单。
 * 设计意图：
 *   - 订单列表（Alice 版式，见 alice/g2/OrdersList.tsx）合并支付订单与余额变动流水，按渠道、状态、入口、退款状态筛选；
 *     点进详情看状态变化与退款记录。商品与对账作为页内可折叠小节，旧的 ?tab= 链接仍然有效。
 *   - 退款金额默认填可退余额；提交后后端调通道退款，追回由 Webhook 或同步推进（详情页可以手动同步）。
 *   - 商品价格以美元填写、存最小货币单位（美分）；花瓣以花瓣填写、存微美元。
 * 关键约束：退款、改商品要 admin；同步与查看 support 即可。退款不可撤销，弹窗要求填写原因并写进审计。
 */
import { IconEdit, IconPlus, IconReceiptRefund, IconRefresh } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { adminApi } from '@ap/api-client/admin'
import { UNITS_PER_PETAL, fmtInt, fmtMinor, petalsToUnits } from '@ap/api-client/money'
import type { OrderDetail as OrderDetailT, OrderStatus, Product } from '@ap/api-client/types'
import { A } from '@ap/i18n/admin'
import { C, errorText } from '@ap/i18n/common'
import { intlTag, useI18n } from '@ap/i18n'
import {
  Badge,
  Button,
  Card,
  CopyField,
  Empty,
  Field,
  Form,
  Input,
  Link,
  Loading,
  Modal,
  Notice,
  PageHead,
  Switch,
  Table,
  fmtDateTime,
  useLoad,
  useRoute,
  useToast,
  type Column,
} from '@ap/ui'
import { useAdmin } from '../session.tsx'
import { AuditList, ReasonModal } from '../shared.tsx'
import { OrdersList } from '../alice/g2/OrdersList.tsx'
import { Section } from '../alice/kit-runtime2.tsx'
import { useU } from '../alice/kit-users.tsx'

type Tab = 'orders' | 'products' | 'reconcile'

const minorDigits = (currency: string) => (['KRW', 'JPY'].includes(currency.toUpperCase()) ? 0 : 2)

/**
 * 充值订单页（Alice 版式）：上方是合并订单列表（OrdersList），下方是「充值商品」和「每日对账」两个可折叠的小节。
 * 兼容旧链接：?tab=products 或 ?tab=reconcile 时对应小节默认展开并滚到视野内。
 */
export function Payments() {
  const { t } = useI18n()
  const { query } = useRoute()
  const tab = (query.get('tab') as Tab) || 'orders'
  const { G } = useU()
  const [open, setOpen] = useState<Record<'products' | 'reconcile', boolean>>({ products: tab === 'products', reconcile: tab === 'reconcile' })
  const toggle = (k: 'products' | 'reconcile') => setOpen((o) => ({ ...o, [k]: !o[k] }))
  useEffect(() => {
    if (tab === 'products' || tab === 'reconcile') {
      setOpen((o) => ({ ...o, [tab]: true }))
      window.setTimeout(() => document.getElementById(`pay-${tab}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50)
    }
  }, [tab])
  return (
    <div id="panel-orders">
      <OrdersList>
        <div id="pay-products">
          <Section
            title={t(G.orders.productsTitle)}
            copy={t(G.orders.productsCopy)}
            onToggle={() => toggle('products')}
            toggle={<span className="u2-note">{t(open.products ? G.orders.collapse : G.orders.expand)}</span>}
          >
            {open.products && (
              <div style={{ padding: 16 }}>
                <ProductsPanel />
              </div>
            )}
          </Section>
        </div>
        <div id="pay-reconcile">
          <Section
            title={t(G.orders.reconcileTitle)}
            copy={t(G.orders.reconcileCopy)}
            onToggle={() => toggle('reconcile')}
            toggle={<span className="u2-note">{t(open.reconcile ? G.orders.collapse : G.orders.expand)}</span>}
          >
            {open.reconcile && (
              <div style={{ padding: 16 }}>
                <ReconcilePanel />
              </div>
            )}
          </Section>
        </div>
      </OrdersList>
    </div>
  )
}

function ProductsPanel() {
  const { t, locale } = useI18n()
  const tag = intlTag(locale)
  const { isAdmin } = useAdmin()
  const products = useLoad(() => adminApi.products(), [])
  const [editing, setEditing] = useState<Product | 'new' | null>(null)
  const cols: Column<Product>[] = [
    { key: 'sku', title: t(A.pay.sku), render: (p) => <span className="ap-mono">{p.sku}</span> },
    { key: 'n', title: t(A.pay.name), render: (p) => p.name },
    { key: 'price', title: t(A.pay.amount), num: true, render: (p) => fmtMinor(p.price_minor, p.currency, tag) },
    { key: 'cr', title: t(A.pay.credit), num: true, render: (p) => fmtInt(p.petals, tag) },
    { key: 'bo', title: t(A.pay.bonus), num: true, render: (p) => fmtInt(p.bonus_petals, tag) },
    { key: 'sort', title: t(A.pay.sort), num: true, muted: true, render: (p) => p.sort },
    { key: 's', title: t(C.status), render: (p) => <Badge tone={p.active ? 'success' : 'neutral'}>{t(p.active ? A.pay.active : A.status.disabled)}</Badge> },
    {
      key: 'a',
      title: '',
      render: (p) =>
        isAdmin && (
          <Button size="sm" variant="ghost" icon={<IconEdit size={14} />} onClick={() => setEditing(p)}>
            {t(C.edit)}
          </Button>
        ),
    },
  ]
  return (
    <>
      {isAdmin && (
        <div className="ap-row" style={{ justifyContent: 'flex-end' }}>
          <Button variant="primary" icon={<IconPlus size={16} />} onClick={() => setEditing('new')}>
            {t(A.pay.createProduct)}
          </Button>
        </div>
      )}
      <Card flush>
        {products.error ? (
          <div className="ap-card__body">
            <Notice tone="danger">{errorText(products.error, locale)}</Notice>
          </div>
        ) : (
          <Table columns={cols} rows={products.data?.data} loading={products.loading} rowKey={(p) => p.sku} />
        )}
      </Card>
      <ProductForm
        product={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          products.reload()
        }}
      />
    </>
  )
}

function ProductForm({ product, onClose, onSaved }: { product: Product | 'new' | null; onClose: () => void; onSaved: () => void }) {
  const { t, locale } = useI18n()
  const toast = useToast()
  const isNew = product === 'new'
  const [sku, setSku] = useState('')
  const [name, setName] = useState('')
  const [price, setPrice] = useState('')
  const [credit, setCredit] = useState('')
  const [bonus, setBonus] = useState('0')
  const [active, setActive] = useState(true)
  const [sort, setSort] = useState('0')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [shown, setShown] = useState<typeof product>(null)
  if (product !== shown) {
    setShown(product)
    setError('')
    if (product) {
      const p = product === 'new' ? null : product
      setSku(p?.sku ?? '')
      setName(p?.name ?? '')
      setPrice(p ? String(p.price_minor / 100) : '')
      setCredit(p ? String(p.credit_units / UNITS_PER_PETAL) : '')
      setBonus(p ? String(p.bonus_units / UNITS_PER_PETAL) : '0')
      setActive(p?.active ?? true)
      setSort(String(p?.sort ?? 0))
    }
  }
  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      await adminApi.putProduct(sku.trim(), {
        name: name.trim(),
        price_minor: Math.round(Number(price) * 100),
        credit_units: petalsToUnits(Number(credit)),
        bonus_units: petalsToUnits(Number(bonus || 0)),
        active,
        sort: Number(sort || 0),
      })
      toast.ok(t(C.saved))
      onSaved()
    } catch (e) {
      setError(errorText(e, locale))
    } finally {
      setBusy(false)
    }
  }
  return (
    <Modal open={product !== null} onClose={onClose} busy={busy} title={isNew ? t(A.pay.createProduct) : sku}>
      <Form onSubmit={submit}>
        {error && <Notice tone="danger">{error}</Notice>}
        <div className="ap-grid2">
          <Field label={t(A.pay.sku)} hint={isNew ? t(A.pay.skuHint) : undefined}>
            <Input mono required disabled={!isNew} pattern="[a-z0-9][a-z0-9_\-]{1,62}" value={sku} onChange={(e) => setSku(e.target.value)} />
          </Field>
          <Field label={t(A.pay.name)}>
            <Input required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t(A.pay.price)}>
            <Input type="number" min={0.01} max={10000} step={0.01} required value={price} onChange={(e) => setPrice(e.target.value)} />
          </Field>
          <Field label={t(A.pay.sort)}>
            <Input type="number" value={sort} onChange={(e) => setSort(e.target.value)} />
          </Field>
          <Field label={t(A.pay.credit)}>
            <Input type="number" min={0.001} step="any" required value={credit} onChange={(e) => setCredit(e.target.value)} />
          </Field>
          <Field label={t(A.pay.bonus)}>
            <Input type="number" min={0} step="any" value={bonus} onChange={(e) => setBonus(e.target.value)} />
          </Field>
        </div>
        <Switch checked={active} onChange={setActive} label={t(A.pay.active)} />
        <div className="ap-row" style={{ justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={busy}>
            {t(C.cancel)}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {t(C.save)}
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

function ReconcilePanel() {
  const { t, locale } = useI18n()
  const data = useLoad(() => adminApi.reconcile(), [])
  type Report = NonNullable<typeof data.data>['reports'][number]
  const cols: Column<Report>[] = [
    { key: 'd', title: t(A.pay.day), nowrap: true, render: (r) => r.day },
    { key: 'ok', title: t(C.status), render: (r) => <Badge tone={r.ok ? 'success' : 'danger'}>{t(r.ok ? A.pay.ok : A.pay.mismatch)}</Badge> },
    {
      key: 'detail',
      title: t(A.audit.detail),
      render: (r) => {
        const s = JSON.stringify(r.detail)
        return (
          <span className="ap-mono ap-muted" title={s} style={{ display: 'inline-block', maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
            {s}
          </span>
        )
      },
    },
    { key: 't', title: t(C.time), nowrap: true, muted: true, render: (r) => fmtDateTime(r.created_at, locale) },
  ]
  if (data.error) return <Notice tone="danger">{errorText(data.error, locale)}</Notice>
  if (!data.data) return <Loading />
  const s = data.data.stuck
  return (
    <>
      <Card title={t(A.pay.stuck)}>
        <dl className="ap-kv">
          <dt>{t(A.pay.unfulfilled)}</dt>
          <dd>{s.unfulfilled > 0 ? <Badge tone="warning">{s.unfulfilled}</Badge> : 0}</dd>
          <dt>{t(A.pay.gaveUp)}</dt>
          <dd>{s.gave_up > 0 ? <Badge tone="danger">{s.gave_up}</Badge> : 0}</dd>
          <dt>{t(A.pay.mismatched)}</dt>
          <dd>{s.mismatched > 0 ? <Badge tone="danger">{s.mismatched}</Badge> : 0}</dd>
        </dl>
      </Card>
      <Card flush title={t(A.pay.reports)}>
        <Table columns={cols} rows={data.data.reports} rowKey={(r) => r.day} empty={<Empty title={t(A.pay.noReports)} />} />
      </Card>
    </>
  )
}

export function OrderDetail({ id }: { id: string }) {
  const { t, locale } = useI18n()
  const tag = intlTag(locale)
  const toast = useToast()
  const { isAdmin } = useAdmin()
  const order = useLoad(() => adminApi.order(id), [id])
  const [refunding, setRefunding] = useState(false)
  const [amount, setAmount] = useState('')
  const [syncing, setSyncing] = useState(false)

  if (order.error) return <Notice tone="danger">{errorText(order.error, locale)}</Notice>
  if (!order.data) return <Loading />
  const o = order.data
  const digits = minorDigits(o.currency)
  const refundable = o.amount_minor - o.refunded_minor
  const canRefund = isAdmin && refundable > 0 && ['fulfilled', 'paid', 'refunded'].includes(o.status)

  const sync = async () => {
    setSyncing(true)
    try {
      order.setData(await adminApi.syncOrder(o.id))
      toast.ok(t(A.pay.synced))
    } catch (e) {
      toast.error(e)
    } finally {
      setSyncing(false)
    }
  }

  type Ev = OrderDetailT['events'][number]
  type Rf = OrderDetailT['refunds'][number]
  const evCols: Column<Ev>[] = [
    { key: 't', title: t(C.time), nowrap: true, muted: true, render: (e) => fmtDateTime(e.at, locale) },
    { key: 'f', title: t(A.pay.from), render: (e) => (e.from ? t(C.orderStatus[e.from as OrderStatus] ?? { ko: e.from, zh: e.from, en: e.from }) : '-') },
    { key: 'to', title: t(A.pay.to), render: (e) => t(C.orderStatus[e.to as OrderStatus] ?? { ko: e.to, zh: e.to, en: e.to }) },
    { key: 'r', title: t(C.reason), render: (e) => e.reason },
  ]
  const rfCols: Column<Rf>[] = [
    { key: 't', title: t(C.time), nowrap: true, muted: true, render: (r) => fmtDateTime(r.created_at, locale) },
    { key: 'k', title: t(A.detail.kind), render: (r) => <Badge>{r.kind}</Badge> },
    { key: 'a', title: t(A.pay.amount), num: true, render: (r) => fmtMinor(r.amount_minor, o.currency, tag) },
    { key: 's', title: t(C.status), render: (r) => <Badge tone={r.status === 'succeeded' ? 'success' : 'neutral'}>{r.status}</Badge> },
    {
      key: 'c',
      title: t(C.petals),
      num: true,
      render: (r) => (r.clawed_at ? `-${fmtInt(r.clawback_units / UNITS_PER_PETAL, tag)}` : <span className="ap-faint">-</span>),
    },
    { key: 'r', title: t(C.reason), render: (r) => r.reason },
  ]

  return (
    <>
      <PageHead
        title={`${t(A.pay.order)} ${o.id.slice(0, 8)}`}
        desc={<Badge tone={o.status === 'fulfilled' ? 'success' : 'neutral'}>{t(C.orderStatus[o.status])}</Badge>}
        actions={
          <>
            <Button icon={<IconRefresh size={16} />} loading={syncing} onClick={sync}>
              {t(A.pay.sync)}
            </Button>
            {canRefund && (
              <Button
                variant="danger"
                icon={<IconReceiptRefund size={16} />}
                onClick={() => {
                  setAmount((refundable / 10 ** digits).toFixed(digits))
                  setRefunding(true)
                }}
              >
                {t(A.pay.refund)}
              </Button>
            )}
          </>
        }
      />
      {o.last_error && <Notice tone="warning">{o.last_error}</Notice>}
      <Card>
        <dl className="ap-kv">
          <dt>ID</dt>
          <dd>
            <CopyField value={o.id} />
          </dd>
          <dt>{t(A.pay.account)}</dt>
          <dd>
            <Link href={`/accounts/${o.account_id}`} className="ap-mono">
              {o.account_id}
            </Link>
          </dd>
          <dt>{t(A.pay.sku)}</dt>
          <dd>{o.sku}</dd>
          <dt>{t(A.pay.amount)}</dt>
          <dd>
            {fmtMinor(o.amount_minor, o.currency, tag)}
            {o.refunded_minor > 0 && <span className="ap-muted"> · {t(A.pay.refunds)} {fmtMinor(o.refunded_minor, o.currency, tag)}</span>}
          </dd>
          <dt>{t(A.pay.petals)}</dt>
          <dd>
            {fmtInt(o.credit_units / UNITS_PER_PETAL, tag)}
            {o.bonus_units > 0 && <span className="ap-muted"> + {fmtInt(o.bonus_units / UNITS_PER_PETAL, tag)}</span>}
          </dd>
          <dt>{t(A.pay.channelIds)}</dt>
          <dd className="ap-mono">
            {[o.channel, o.channel_session_id, o.channel_txn_id].filter(Boolean).join(' · ') || '-'}
          </dd>
          <dt>{t(A.pay.attempts)}</dt>
          <dd>{o.fulfill_attempts ?? 0}</dd>
          <dt>{t(C.createdAt)}</dt>
          <dd>{fmtDateTime(o.created_at, locale)}</dd>
          <dt>{t(A.pay.paidAt)}</dt>
          <dd>{fmtDateTime(o.paid_at, locale) || '-'}</dd>
        </dl>
      </Card>
      <Card flush title={t(A.pay.events)}>
        <Table columns={evCols} rows={o.events} rowKey={(e) => `${e.at}-${e.to}`} />
      </Card>
      {o.refunds.length > 0 && (
        <Card flush title={t(A.pay.refunds)}>
          <Table columns={rfCols} rows={o.refunds} rowKey={(r) => r.id} />
        </Card>
      )}
      <Card flush title={t(A.nav.audit)}>
        <AuditList fetchPage={(c) => adminApi.billingAudit(c, o.id)} deps={[o.id, o.status, o.refunded_minor]} />
      </Card>

      <ReasonModal
        open={refunding}
        danger
        title={t(A.pay.refundTitle)}
        lead={t(A.pay.refundHint, { max: fmtMinor(refundable, o.currency, tag) })}
        confirmLabel={t(A.pay.refund)}
        onClose={() => setRefunding(false)}
        onSubmit={async (reason) => {
          const minor = Math.round(Number(amount) * 10 ** digits)
          await adminApi.refund(o.id, minor, reason)
          setRefunding(false)
          toast.ok(t(A.pay.refunded))
          order.reload()
        }}
      >
        <Field label={t(A.pay.refundAmount)}>
          <Input
            type="number"
            required
            min={1 / 10 ** digits}
            max={refundable / 10 ** digits}
            step={1 / 10 ** digits}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>
      </ReasonModal>
    </>
  )
}
